import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import { selectInstoreIsEnabled } from 'core/selectors/app';
import { selectGspConfig } from 'core/selectors/services';
import { useGetAllInstoreOffers } from 'core/modules/Instore2/hooks/useGetInstoreOffers';
import { scrollToElement } from 'core/utils/helpers/scrollToElement';
import { fetchMerchantById } from 'core/actions/ocapi';
import { getParamsFromSearch } from 'core/utils/helpers/getParamsFromSearch';
import MeInstoreOnlyOffer from 'core/modules/Instore2/components/MePage/MeInstoreOnlyOffer/MeInstoreOnlyOffer';
import FeaturedInstoreOffers from 'core/modules/Instore2/components/FeaturedInstoreOffers/FeaturedInstoreOffers';

import {
  selectMerchantExperienceConfig, selectMerchantExperienceIsLoading,
  selectMerchantExperienceIsLoaded, selectMerchantExperienceMerchants,
} from './selectors';

import {
  clearMerchantExperienceState, getMerchantExperienceResponseFromPage, sendAnalyticsPageViewAction,
} from './actions';

import { getActiveMerchants, getMerchantExperiencePageViewType } from './utils/getMerchantExperiencePageViewType';

import {
  MERCHANT_EXPERIENCE_TYPE_SINGLE, MERCHANT_EXPERIENCE_TYPE_MULTIPLE,
  MERCHANT_EXPERIENCE_TYPE_SUSPENDED, MERCHANT_EXPERIENCE_TYPE_DIRECT_LINK,
} from './actions/types';

// Sub pages
import MerchantExperienceViewSingle from
  './components/pageViews/MerchantExperienceViewSingle/MerchantExperienceViewSingle';
import MerchantExperienceViewMultiple from
  './components/pageViews/MerchantExperienceViewMultiple/MerchantExperienceViewMultiple';
import MerchantExperienceViewSuspended from
  './components/pageViews/MerchantExperienceViewSuspended/MerchantExperienceViewSuspended';

import './MerchantExperience.scss';

function MerchantExperience(props) {
  const config = useSelector(selectMerchantExperienceConfig);
  const isLoading = useSelector(selectMerchantExperienceIsLoading);
  const isLoaded = useSelector(selectMerchantExperienceIsLoaded);
  const merchants = useSelector(selectMerchantExperienceMerchants);
  const gspConfig = useSelector(selectGspConfig);
  const location = useLocation();
  const isInstoreEnabled = useSelector(selectInstoreIsEnabled);

  const { keywords, gmid } = getParamsFromSearch(location.search);

  const { offers: instoreOffers, isLoaded: instoreOffersIsLoaded } = useGetAllInstoreOffers();
  const instoreOffer = instoreOffers.find(({ carteraMerchantId }) => carteraMerchantId === Number(gmid));

  const dispatch = useDispatch();

  useEffect(() => {
    const getMerchantsFromAPI = () => {
      if (!isLoaded && !isLoading && !gmid) {
        const { api: { params, options } } = config;
        dispatch(fetchMerchantById(gmid, params, options));
      }
    };
    dispatch(getMerchantExperienceResponseFromPage());
    // Uncomment to use an API call to get the merchants data instead of using a smarty-driven object
    getMerchantsFromAPI();
    if (location.hash) {
      setTimeout(() => { scrollToElement(location.hash, 500, 10); }, 500);
    }

    return () => {
      dispatch(clearMerchantExperienceState());
    };
  }, []);

  useEffect(() => {
    if (isLoaded) {
      dispatch(sendAnalyticsPageViewAction(merchants));
    }
  }, [isLoaded]);

  let CurrentPageView;

  if (!isLoaded || !gmid) {
    return null;
  }

  const activeMerchants = getActiveMerchants(merchants);

  switch (getMerchantExperiencePageViewType(merchants, keywords, instoreOffer, instoreOffersIsLoaded)) {
    case MERCHANT_EXPERIENCE_TYPE_MULTIPLE: {
      CurrentPageView = props.ViewMultiple || MerchantExperienceViewMultiple;
      return <CurrentPageView merchants={merchants} config={config} keywords={keywords} gspConfig={gspConfig} />;
    }
    case MERCHANT_EXPERIENCE_TYPE_SINGLE:
    case MERCHANT_EXPERIENCE_TYPE_DIRECT_LINK: {
      CurrentPageView = props.ViewSingle || MerchantExperienceViewSingle;
      return (<CurrentPageView
        merchant={activeMerchants[0]}
        config={config}
        keywords={keywords}
      />);
    }
    case MERCHANT_EXPERIENCE_TYPE_SUSPENDED: {
      if (isInstoreEnabled && !instoreOffersIsLoaded) {
        return null;
      }

      if (isInstoreEnabled && instoreOffersIsLoaded && instoreOffer) {
        return <><MeInstoreOnlyOffer offer={instoreOffer} /><FeaturedInstoreOffers /></>;
      }

      CurrentPageView = props.ViewSuspended || MerchantExperienceViewSuspended;
      return <CurrentPageView merchant={merchants[0]} config={config} keywords={keywords} />;
    }
    default: {
      return null;
    }
  }
}

export default (props) =>
  <ComponentErrorBoundary><MerchantExperience {...props} /></ComponentErrorBoundary>;
